import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDataGrid from '@inovua/reactdatagrid-enterprise';
import '@inovua/reactdatagrid-community/index.css';
import { userAppContext } from '../../Context/Context.jsx';
import buildResultEndpoint from '../../utils/buildResultEndpoint';
import { filterTypes } from './SetupTablesResources.jsx';
import fetchEndpoint from '../../services/fetchEndpoint.jsx';
import Filters from './Filters.jsx';
import ErrorMessage from '../UI/ErrorMessage.jsx';
import { getSourceList } from '../../modules/search/searchGrid/helpers/sourceListHelpers.ts';
import { isEmpty, isArray } from 'lodash';
import { decodeData, encodeData } from '../../utils/encodeUtils.ts';
import ActiveFiltersBar from '../UI/ActiveFiltersBar.tsx';
import SharedDialog from '../../global/Modal/Modal.tsx';
import { getFilterLabel } from '../../modules/search/searchGrid/helpers/getFiltersLabels.ts';
import { getFiltersForUser, setFiltersForUser } from '../../utils/filtersStorage.ts';
import Alert from '../../global/Alert/Alert.tsx';

const SHOW_SUMMARIZE = process.env.REACT_APP_SHOW_SUMMARIZE === 'true';

function Grid({
  columns,
  columnVisibilityModel,
  columnOrder,
  setColumnOrder,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  setTotalResults,
  getFilterValueRef,
  onData,
  ...rest
}) {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    authorization,
    setIsAuthenticated,
    groups,
    setGroups,
    setAuthError,
    setAuthorization,
    restrictGridDisplay,
    restrictGridResultCount,
    restrictBrowseAll,
    restrictSources,
    restrictChemical,
    mappingContent,
    user,
  } = userAppContext();

  // Refs to avoid unwanted scrolling
  const gridContainerRef = useRef(null);

  // State Management
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [gridRef, setGridRef] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);
  const [orderByParam, setOrderByParam] = useState(undefined);
  const [order, setOrder] = useState(undefined);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [URLWithFilters, setURLWithFilters] = useState('');
  const [showCopiedAlert, setShowCopiedAlert] = useState(false);
  const [pendingFiltersToSave, setPendingFiltersToSave] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [filtersLoadedFromURL, setFiltersLoadedFromURL] = useState(false);

  const [defaultFilterValue, setDefaultFilterValue] = useState(() => {
    if (Array.isArray(rest.defaultFilterValue) && rest.defaultFilterValue.length > 0) {
      return [...Filters.filter(f => f), ...rest.defaultFilterValue];
    }
    return Filters.filter(f => f);
  });

  const [incompleteFilterMessage, setIncompleteFilterMessage] = useState(null);
  const [selectedRow, setSelected] = useState([]);

  const onSelectionChange = useCallback(
    ({ selected, data }) => {
      const areItemsSelected = Object.keys(selected).length > 0 || !isEmpty(selected) || selected === true;
      const dataToSend = isArray(data) ? data : Object.values(selected);

      setSelected(selected);
      onData(areItemsSelected, dataToSend);
    },
    [selectedRow, onData],
  );

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');

  const defaultSortInfo = [];
  const sortInfo = orderByParam ? { dir: isTrial ? 1 : order, name: orderByParam } : defaultSortInfo;

  const validSourceList = getSourceList({ tab, restrictSources });
  const allValidSources = Array.isArray(validSourceList)
    ? validSourceList
    : Object.values(validSourceList).flat();

  const isInvalidSource = source && !allValidSources.includes(source);

  if (getFilterValueRef) {
    getFilterValueRef.current = () => defaultFilterValue;
  }

  // Helper function to determine pageSizes
  function getPageSizes() {
    if (!isAuthenticated) return [10];
    if (isTrial) return [10, 30];
    if (restrictBrowseAll) return [10, 30];
    return [10, 20, 25, 40, 50, 100];
  }

  // Helper function to check if filter is incomplete
  function checkIncompleteFilters(filterValue) {
    const incompleteFilters = [];

    filterValue.forEach(filter => {
      if (filter.name === 'reference' && filter.value && filter.value.length < 8) {
        incompleteFilters.push('reference');
      }
      if (filter.name === 'pmid' && filter.value && filter.value.length < 8) {
        incompleteFilters.push('PMID');
      }
    });

    if (incompleteFilters.length > 0) {
      const filterNames = incompleteFilters.join(' y ');
      return `You must write the full ${filterNames} to filter`;
    }

    return null;
  }

  // Effect for data fetching
  useEffect(() => {
    if (!(view && source && tab && searchItems)) return;

    // Check for incomplete filters
    const incompleteMessage = checkIncompleteFilters(defaultFilterValue);
    setIncompleteFilterMessage(incompleteMessage);

    // If there are incomplete filters, don't make the API call
    if (incompleteMessage) {
      setDataSource(Promise.resolve({ data: [], count: 0 }));
      setTotalResults(0);
      return;
    }

    let urlEndpoint = buildResultEndpoint({
      view,
      source,
      tab,
      searchItems,
      isEvidence,
      isMapping,
      mappingContent,
      pageNum: restrictGridResultCount || (restrictBrowseAll && view === 'ALL') ? 0 : skip / limit,
      pageSize: limit,
      orderByParam,
      order,
      defaultFilterValue,
    });

    if (!isAuthenticated && skip !== 0) {
      setShowPopup(true);
      return;
    }

    if (restrictBrowseAll && view === 'ALL' && skip !== 0) {
      setShowPopup(true);
      return;
    }

    if (isTrial && skip !== 0) {
      setShowPopup(true);
      return;
    }

    if (!urlEndpoint) return;
    urlEndpoint = !isAuthenticated ? `${urlEndpoint}&skey=unauth` : urlEndpoint;

    setDataSource(prev => {
      const result = fetchEndpoint(
        urlEndpoint,
        authorization,
        setAuthorization,
        setIsAuthenticated,
        setGroups,
        setAuthError,
        prev,
      )
        .then(res => {
          const data = res?.payload;
          const totalCount = res?.paging ? res.paging.totalElements : 0;

          if (data) {
            // Update result count state
            setTotalResults(totalCount);
            setIsSessionExpired(false);
            return Promise.resolve({ data, count: parseInt(totalCount) });
          }

          // If 403, session has expired
          if (res?.status === 403) setIsSessionExpired(true);
          return Promise.resolve({ data: [], count: 0 });
        })
        .catch(error => {
          if (process.env.NODE_ENV === 'production') {
            return Promise.resolve({ data: [], count: 0 });
          } else {
            console.error('Error:', error);
            throw new Error(error);
          }
        });

      return result;
    });
  }, [view, source, tab, searchItems, skip, limit, order, orderByParam, defaultFilterValue]);

  // Sorting and Filtering
  const onSortInfoChange = useCallback(value => {
    const [sort] = Array.isArray(value) ? value : [value];
    setOrder(sort?.dir);
    setOrderByParam(sort?.name);
  }, []);

  useEffect(() => {
    if (filtersLoadedFromURL) return;

    const p = new URLSearchParams(location.search);
    const filterToken = p.get('f');

    if (filterToken) {
      const decodedFilters = decodeData(filterToken);
      if (decodedFilters?.length) {
        const relevantFilters = decodedFilters.filter(f => f.isEvidence === isEvidence);

        if (relevantFilters.length > 0) {
          setDefaultFilterValue(() => {
            const baseFiltersMap = new Map(Filters.filter(f => f).map(f => [f.name, { ...f, value: '' }]));

            relevantFilters.forEach(f => {
              baseFiltersMap.set(f.name, f);
            });

            return Array.from(baseFiltersMap.values());
          });

          setFiltersLoadedFromURL(true);
        }
      }
    } else {
      setFiltersLoadedFromURL(true);
    }
  }, [location.search, isEvidence, filtersLoadedFromURL]);

  const onFilterValueChange = useCallback(
    value => {
      const scrollX = gridContainerRef.current?.scrollLeft || 0;
      setDefaultFilterValue(value);

      const filters = value.filter(v => v.value);

      const filtersWithEvidence = filters.map(filter => ({ ...filter, isEvidence }));

      setPendingFiltersToSave(filtersWithEvidence);

      if (filtersWithEvidence.length > 0) {
        const encodedFilters = encodeData(filtersWithEvidence);
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.set('f', encodedFilters);

        window.history.replaceState({}, '', currentUrl.href);
        setURLWithFilters(currentUrl.href);
      } else {
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.delete('f');
        window.history.replaceState({}, '', currentUrl.href);
        setURLWithFilters('');
      }

      setTimeout(() => {
        if (gridContainerRef.current) gridContainerRef.current.scrollLeft = scrollX;
      }, 0);
    },
    [isEvidence],
  );

  const handleColumnOrderChange = useCallback(
    newOrder => {
      if (setColumnOrder) {
        setColumnOrder(newOrder);
      }
    },
    [setColumnOrder],
  );

  const handleSaveFilters = useCallback(() => {
    if (!pendingFiltersToSave) return;

    const existingFilters = getFiltersForUser(user);

    const searchContext = {
      id: `search-${Date.now()}`,
      tab,
      view,
      source,
      idents: searchItems ? searchItems.map(item => item.id).join('-') : '',
      identsNames: searchItems ? searchItems.map(item => item.name).join(', ') : '',
      isEvidence,
      filters: pendingFiltersToSave,
      savedAt: new Date().toISOString(),
    };

    const updatedFilters = [...existingFilters, searchContext];

    setFiltersForUser(user, updatedFilters);

    setAlertMessage('Filters saved successfully');
    setShowCopiedAlert(true);
    setTimeout(() => setShowCopiedAlert(false), 3000);
    setShowFiltersModal(false);
  }, [pendingFiltersToSave, isEvidence, tab, view, source, searchItems, user]);

  const handleClearAllFilters = useCallback(() => {
    const baseFilters = Filters.filter(f => f).map(f => ({ ...f, value: '' }));

    setSkip(0);

    try {
      const url = new URL(window.location);
      url.searchParams.delete('f');
      window.history.replaceState({}, '', url);
      setURLWithFilters('');
    } catch (e) {
      // ignore URL errors
    }

    setDefaultFilterValue(baseFilters);
    if (gridRef?.current) {
      try {
        gridRef.current.setFilterValue(baseFilters);
      } catch (e) {
        // ignore if grid not ready
      }
    }

    onFilterValueChange(baseFilters);
  }, [onFilterValueChange, gridRef]);

  const handleCopyURL = useCallback(() => {
    try {
      navigator.clipboard.writeText(URLWithFilters);
      setAlertMessage('Copied to clipboard');
      setShowCopiedAlert(true);
      setTimeout(() => setShowCopiedAlert(false), 3000);
    } catch (err) {
      console.error('❌ Error to copy URL:', err);
    }
  }, [URLWithFilters]);

  // Render custom context menu
  const renderColumnContextMenu = useCallback(menuProps => {
    const menuPropsLength = menuProps.items?.length;
    menuProps.items = menuProps.items.splice(0, menuPropsLength - 2);
  }, []);

  const handleClosePopup = useCallback(() => {
    setShowPopup(false);
    setSkip(0);
  }, []);

  const handleRemoveFilter = useCallback(
    filterToRemove => {
      const newFilters = defaultFilterValue.map(f => {
        if (
          f.name === filterToRemove.name &&
          (f.type !== 'number' || f.operator === filterToRemove.operator)
        ) {
          return { ...f, value: '' };
        }
        return f;
      });

      setDefaultFilterValue(newFilters);

      if (gridRef?.current) {
        gridRef.current.setFilterValue(newFilters);
      }

      onFilterValueChange(newFilters);
    },
    [defaultFilterValue, onFilterValueChange, gridRef],
  );

  const prevTabRef = useRef(tab);

  useEffect(() => {
    if (prevTabRef.current !== tab) {
      const baseFilters = Filters.filter(f => f).map(f => ({ ...f, value: '' }));
      setDefaultFilterValue(baseFilters);
      onFilterValueChange(baseFilters);
      setFiltersLoadedFromURL(false);
      prevTabRef.current = tab;
    }
  }, [tab]);

  useEffect(() => {
    if (gridRef) {
      window.gridRef = gridRef;
    }
    return () => {
      window.gridRef = null;
    };
  }, [gridRef]);

  // Empty state message
  const emptyText = (() => {
    // Priority: incomplete filter message
    if (incompleteFilterMessage) {
      return <b>{incompleteFilterMessage}</b>;
    }

    // Default empty messages
    if (dataSource.length == null) {
      return <b>{isAcademic ? 'data unavailable for the Academic Plan' : 'no data available'}</b>;
    }

    return null;
  })();

  // restriction handling
  if (
    (restrictGridDisplay && view === 'ALL') ||
    (restrictChemical && tab === 'CHEMICALS') ||
    (restrictChemical && view === 'CHEMICALS') ||
    (restrictChemical && isMapping && mappingContent?.tabSegment.includes('chem')) ||
    (restrictChemical && isMapping && mappingContent?.name === 'numberPmidsWithChemsIncludedInEvidence')
  ) {
    return (
      <ErrorMessage
        title={'This content is not available with your current profile'}
        subtitle={
          <div>
            <button onClick={() => navigate('/plans')}>
              View our <a href="/plans">plans</a>
            </button>
          </div>
        }
      />
    );
  }

  // no columns selected handling
  if (!columns || columns.length === 0) {
    return (
      <ErrorMessage
        title={'No columns selected'}
        subtitle={<span>Please select columns to display in the grid.</span>}
        style={{ minHeight: 537 }}
      />
    );
  }

  // Error handling
  if (isSessionExpired) {
    return <ErrorMessage title={'Session expired'} subtitle={<p>Please refresh the page</p>} />;
  }

  return (
    <>
      {isInvalidSource && (
        <ErrorMessage
          title={`No data available for "${source}" in ${tab}`}
          subtitle="Please remove or change the source filter."
        />
      )}

      {showCopiedAlert && <Alert message={alertMessage} type="success" />}

      <ActiveFiltersBar
        filters={defaultFilterValue}
        onRemoveFilter={handleRemoveFilter}
        onClearAll={handleClearAllFilters}
        copyURL={handleCopyURL}
        isEvidence={isEvidence}
      />

      <ReactDataGrid
        idProperty="assocID"
        columns={columns}
        style={{ minHeight: 537 }}
        renderColumnContextMenu={renderColumnContextMenu}
        licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE}
        filterTypes={filterTypes}
        pagination={true}
        enableFiltering={true}
        nativeScroll={true}
        limit={limit}
        skip={skip}
        onLimitChange={setLimit}
        pageSizes={getPageSizes()}
        columnUserSelect
        defaultFilterValue={defaultFilterValue}
        onFilterValueChange={onFilterValueChange}
        dataSource={dataSource}
        showColumnMenuFilterOptions={false}
        showColumnMenuGroupOptions={false}
        columnVisibilityModel={columnVisibilityModel}
        reorderColumns={true}
        columnOrder={columnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        onSkipChange={setSkip}
        onReady={setGridRef}
        sortInfo={sortInfo}
        onSortInfoChange={onSortInfoChange}
        scrollTopOnFilter={false}
        scrollTopOnSort={false}
        emptyText={emptyText}
        scrollProps={{
          autoHide: false,
          alwaysShowTrack: true,
          enableSmoothScroll: false,
        }}
        checkboxColumn={isEvidence && SHOW_SUMMARIZE}
        selected={selectedRow}
        onSelectionChange={onSelectionChange}
      />
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            {!isAuthenticated && (
              <p>
                As an unregistered user you are viewing only 10 results ordered by score for each query. To
                view more, register for an account. Learn more about our <a href="/plans">plans here</a>.
              </p>
            )}
            {restrictBrowseAll && (
              <p>
                As an Academic user you are viewing only 30 results ordered by score for each query. To view
                more, upgrade your account. Learn more about our <a href="/plans">plans here</a>.
              </p>
            )}
            {isTrial && (
              <p>
                Please note that the examples shown on this page only display the top-30 results ordered by
                score. To view more, <a href="/Profile-area">upgrade</a> your account. Learn more about our{' '}
                <a href="/plans">plans here</a>.
              </p>
            )}
            <button onClick={handleClosePopup}>Close</button>
          </div>
        </div>
      )}

      <SharedDialog
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        title="Do you want to save this search?"
        confirmText="Yes"
        cancelText="No"
        hasConfirmBtn
        onConfirm={handleSaveFilters}
      >
        <div className="grid">
          <span>
            <strong>Tab: </strong>
            {tab}
          </span>
          <span>
            <strong>View: </strong>
            {view}
          </span>
          <span>
            <strong>Source: </strong>
            {source}
          </span>
          {!isEmpty(searchItems) && (
            <span>
              <strong>Idents: </strong>
              {searchItems.map(item => item.id).join(', ')}
            </span>
          )}
          <div>
            {pendingFiltersToSave && pendingFiltersToSave.length > 0 && (
              <>
                <strong>Filters: </strong>
                {pendingFiltersToSave.map(filter => getFilterLabel(filter)).join(', ')}
              </>
            )}
          </div>
        </div>
      </SharedDialog>
    </>
  );
}

export default Grid;
